import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createActionPlan, fetchBusinessProcesses, fetchBusinessUnit, fetchActionPlansByBusinessUnit } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function ActionPlanCreatePage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans`;

  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [existingActionPlans, setExistingActionPlans] = useState([]);
  const ACTION_PLAN_TYPES = ["Plan", "Do", "CheckAct", "None"];

  const [form, setForm] = useState({
    actionPlanName: "",
    actionPlanDescription: "",
    businessProcessId: "",
    businessProcessName: "",
    actionPlanType: "None",
  });

  useEffect(() => {
    (async () => {
      try {
        const [procs, unit, plans] = await Promise.all([
          fetchBusinessProcesses(businessDomainId),
          fetchBusinessUnit(businessUnitId),
          fetchActionPlansByBusinessUnit(businessUnitId),
        ]);
        setBusinessProcesses(Array.isArray(procs) ? procs.filter((p) => p.valueChain !== true) : []);
        setBusinessUnit(unit);
        setExistingActionPlans(Array.isArray(plans) ? plans : []);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, businessUnitId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleBusinessProcessChange = (e) => {
    const selected = businessProcesses.find((p) => p.businessProcessId === e.target.value);
    setForm({
      ...form,
      businessProcessId: e.target.value,
      businessProcessName: selected ? selected.businessProcessName : "",
      actionPlanName: selected ? selected.businessProcessName : form.actionPlanName,
      actionPlanDescription: selected ? selected.businessProcessName : form.actionPlanDescription,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isDuplicate = existingActionPlans.some(
      (p) => p.actionPlanName === form.actionPlanName.trim()
    );
    if (isDuplicate) {
      notify.error(t("actionPlan.error.duplicateName"));
      return;
    }
    try {
      await createActionPlan({
        companyId: COMPANY_ID,
        actionPlanName: form.actionPlanName,
        actionPlanDescription: form.actionPlanDescription || null,
        businessProcessId: form.businessProcessId,
        businessProcessName: form.businessProcessName,
        businessUnitId: businessUnit?.businessUnitId || businessUnitId,
        businessUnitName: businessUnit?.businessUnitName || "",
        actionPlanType: form.actionPlanType,
      });
      notify.success(t("message.create.success"));
      navigate(basePath, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("actionPlan.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* アクションプラン名 */}
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("actionPlan.name")}
              name="actionPlanName"
              value={form.actionPlanName}
              onChange={handleChange}
            />
          </Grid>

          {/* 説明 */}
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("common.description")}
              name="actionPlanDescription"
              value={form.actionPlanDescription}
              onChange={handleChange}
            />
          </Grid>

          {/* ビジネスプロセス選択 */}
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("actionPlan.businessProcess")}</InputLabel>
              <Select
                value={form.businessProcessId}
                onChange={handleBusinessProcessChange}
                label={t("actionPlan.businessProcess")}
              >
                {businessProcesses.map((p) => (
                  <MenuItem key={p.businessProcessId} value={p.businessProcessId}>
                    {p.businessProcessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* アクションプランタイプ */}
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("actionPlan.type")}</InputLabel>
              <Select
                value={form.actionPlanType}
                name="actionPlanType"
                onChange={handleChange}
                label={t("actionPlan.type")}
              >
                {ACTION_PLAN_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{t(`actionPlanType.${type}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
